import { useNavigate, useLocation } from "react-router-dom";
import { useWorkspaceStore } from "@/store/useWorkspaceStore";
import { resolveUrl } from "@/utils/workspace-url";

/** uni-app 原始路径 → React 路由路径映射 */
const URL_MAP: Record<string, string> = {
  "/pages/service_staff/index": "/workspace",
  "/pages/service_staff/order": "/workspace/order",
  "/pages/service_staff/my": "/workspace/profile",
  // 兼容后端返回的其他 uni-app 路径格式
  "/subExpertPackage/pageStaffInfo/index": "/workspace/profile",
  "/subExpertPackage/home/index": "/workspace",
  "/subExpertPackage/order/index": "/workspace/order",
};

/** 默认导航（config 未加载时的兜底） */
const DEFAULT_ITEMS = [
  {
    id: "home" as const,
    name: "首页",
    pic: "",
    pics: "",
    url: "/pages/service_staff/index",
  },
  {
    id: "order" as const,
    name: "订单",
    pic: "",
    pics: "",
    url: "/pages/service_staff/order",
  },
  {
    id: "profile" as const,
    name: "我的",
    pic: "",
    pics: "",
    url: "/pages/service_staff/my",
  },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useWorkspaceStore((s) => s.config);

  const navItems = (config?.staff_navigation_bar?.length
    ? config.staff_navigation_bar
    : DEFAULT_ITEMS) as Array<{
    id: number | string;
    name: string;
    pic: string;   // 选中图标
    pics: string;  // 未选中图标
    url: string;
  }>;

  /** 判断当前项是否激活（最长前缀匹配：避免 /workspace 误匹配 /workspace/order） */
  const isActive = (item: typeof navItems[0]) => {
    const mappedPath = URL_MAP[item.url] ?? resolveUrl(item.url);
    // 精确匹配
    if (location.pathname === mappedPath) return true;
    // 前缀匹配：当前路径必须以 mappedPath + "/" 开头
    if (!location.pathname.startsWith(mappedPath + "/")) return false;

    // ★ 排除其他 Tab 的更具体路径（确保只有最精确的 Tab 高亮）
    const otherPaths = navItems
      .filter((other) => other.url !== item.url)
      .map((other) => URL_MAP[other.url] ?? resolveUrl(other.url));
    for (const other of otherPaths) {
      if (location.pathname.startsWith(other + "/") || location.pathname === other) {
        return false; // 其他 Tab 更匹配，当前不激活
      }
    }
    return true;
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{
        background: "#fff",
        borderTop: "1px solid #f0f0f0",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <div className="flex items-center justify-around px-5 pt-2 pb-3">
        {navItems.map((item) => {
          const active = isActive(item);
          // 先查 URL_MAP，再用 resolveUrl 兜底
          const targetPath = URL_MAP[item.url] ?? resolveUrl(item.url);

          return (
            <button
              key={item.id}
              onClick={() => targetPath && navigate(targetPath)}
              className="flex flex-col items-center gap-1 cursor-pointer active:scale-95 transition-transform"
              style={{ minWidth: 56 }}
            >
              {/* 动态图标（优先使用图片，无图片时用文字色区分） */}
              {item.pic ? (
                <img
                  src={active ? item.pic : item.pics}
                  alt={item.name}
                  className="w-[27px] h-[27px]"
                  style={{ objectFit: "contain" }}
                />
              ) : (
                <span
                  className="text-base"
                  style={{ color: active ? "#FF6B35" : "#C0C4CC", lineHeight: 1 }}
                >
                  ●
                </span>
              )}
              <span
                className="text-[11px]"
                style={{
                  color: active ? "#FF6B35" : "#999",
                  fontWeight: active ? 600 : 400,
                }}
              >
                {item.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
